import { useState, useEffect, useRef } from 'react';
import { createClient } from '@supabase/supabase-js';
import { useNavigate } from 'react-router-dom';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface Tractor {
  id: string;
  name: string;
  slug: string;
  brand: string;
  model: string;
  year: number;
  hours: number;
  price: number;
  condition: string;
  description: string;
  main_image: string;
  image_2?: string;
  image_3?: string;
  image_4?: string;
  engine?: string;
  horsepower?: string;
  transmission?: string;
  hydraulics?: string;
  weight?: string;
  fuel_capacity?: string;
  features?: string[];
  contact_phone?: string;
  contact_email?: string;
  is_active: boolean;
}

export default function AdminPage() {
  const [tractors, setTractors] = useState<Tractor[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingTractor, setEditingTractor] = useState<Tractor | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const navigate = useNavigate();

  const ADMIN_PASSWORD = '5321';

  useEffect(() => {
    const auth = sessionStorage.getItem('admin_auth');
    if (auth === 'true') {
      setIsAuthenticated(true);
      loadTractors();
    } else {
      setLoading(false);
    }
  }, []);

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      sessionStorage.setItem('admin_auth', 'true');
      setIsAuthenticated(true);
      loadTractors();
    } else {
      alert('Incorrect password');
    }
  };

  const loadTractors = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('tractors')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error loading tractors:', error);
    } else {
      setTractors(data || []);
    }
    setLoading(false);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this tractor?')) return;

    const { error } = await supabase
      .from('tractors')
      .delete()
      .eq('id', id);

    if (error) {
      alert('Error deleting tractor');
      console.error(error);
    } else {
      loadTractors();
    }
  };

  const handleToggleActive = async (id: string, currentStatus: boolean) => {
    const { error } = await supabase
      .from('tractors')
      .update({ is_active: !currentStatus })
      .eq('id', id);

    if (error) {
      alert('Error updating tractor status');
      console.error(error);
    } else {
      loadTractors();
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-lock-line text-3xl text-white"></i>
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Admin Login</h1>
            <p className="text-sm text-gray-600 mt-2">Enter password to manage tractors</p>
          </div>
          <form onSubmit={handleLogin}>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter admin password"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent outline-none text-sm"
              required
            />
            <button
              type="submit"
              className="w-full mt-4 bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors whitespace-nowrap"
            >
              Login
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/')}
                className="text-gray-600 hover:text-gray-900 transition-colors"
              >
                <i className="ri-arrow-left-line text-xl"></i>
              </button>
              <h1 className="text-2xl font-bold text-gray-900">Tractor Management</h1>
            </div>
            <button
              onClick={() => {
                setEditingTractor(null);
                setShowForm(true);
              }}
              className="bg-green-600 text-white px-6 py-2.5 rounded-lg font-semibold hover:bg-green-700 transition-colors flex items-center gap-2 whitespace-nowrap"
            >
              <i className="ri-add-line text-lg"></i>
              Add Tractor
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-green-600 border-t-transparent"></div>
          </div>
        ) : (
          <div className="grid gap-6">
            {tractors.map((tractor) => (
              <div
                key={tractor.id}
                className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex gap-6">
                  <img
                    src={tractor.main_image}
                    alt={tractor.name}
                    className="w-32 h-32 object-cover rounded-lg"
                  />
                  <div className="flex-1">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">{tractor.name}</h3>
                        <p className="text-sm text-gray-600 mt-1">
                          {tractor.year} • {tractor.hours.toLocaleString()} hours • {tractor.condition}
                        </p>
                        <p className="text-2xl font-bold text-green-600 mt-2">
                          ${tractor.price.toLocaleString()}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            tractor.is_active
                              ? 'bg-green-100 text-green-700'
                              : 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {tractor.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-4">
                      <button
                        onClick={() => {
                          setEditingTractor(tractor);
                          setShowForm(true);
                        }}
                        className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium whitespace-nowrap"
                      >
                        <i className="ri-edit-line mr-1"></i>
                        Edit
                      </button>
                      <button
                        onClick={() => handleToggleActive(tractor.id, tractor.is_active)}
                        className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium whitespace-nowrap"
                      >
                        <i className={`ri-${tractor.is_active ? 'eye-off' : 'eye'}-line mr-1`}></i>
                        {tractor.is_active ? 'Hide' : 'Show'}
                      </button>
                      <button
                        onClick={() => handleDelete(tractor.id)}
                        className="px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors text-sm font-medium whitespace-nowrap"
                      >
                        <i className="ri-delete-bin-line mr-1"></i>
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Form Modal */}
      {showForm && (
        <TractorForm
          tractor={editingTractor}
          onClose={() => {
            setShowForm(false);
            setEditingTractor(null);
          }}
          onSave={() => {
            setShowForm(false);
            setEditingTractor(null);
            loadTractors();
          }}
        />
      )}
    </div>
  );
}

function ImageUploadField({
  label,
  value,
  onChange,
  required,
}: {
  label: string;
  value: string;
  onChange: (url: string) => void;
  required?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setError('');

    const ext = file.name.split('.').pop();
    const fileName = `tractor-${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;

    const { data, error: uploadError } = await supabase.storage
      .from('tractor-images')
      .upload(fileName, file, { upsert: true });

    if (uploadError) {
      setError('Upload failed. Try again.');
      console.error(uploadError);
      setUploading(false);
      return;
    }

    const { data: urlData } = supabase.storage
      .from('tractor-images')
      .getPublicUrl(data.path);

    onChange(urlData.publicUrl);
    setUploading(false);
  };

  return (
    <div>
      <label className="block text-sm font-semibold text-gray-700 mb-2">
        {label} {required && '*'}
      </label>

      <div className="flex gap-3 items-start">
        {/* Upload Button */}
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="flex-shrink-0 flex flex-col items-center justify-center w-24 h-24 border-2 border-dashed border-green-400 rounded-xl bg-green-50 hover:bg-green-100 transition-colors cursor-pointer disabled:opacity-50"
        >
          {uploading ? (
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-green-600 border-t-transparent"></div>
          ) : (
            <>
              <i className="ri-camera-line text-2xl text-green-600"></i>
              <span className="text-xs text-green-700 font-medium mt-1 text-center leading-tight">
                Tap to Upload
              </span>
            </>
          )}
        </button>

        {/* Preview or URL input */}
        <div className="flex-1 flex flex-col gap-2">
          {value ? (
            <div className="relative w-full h-24 rounded-xl overflow-hidden border border-gray-200 bg-gray-50">
              <img
                src={value}
                alt="Preview"
                className="w-full h-full object-cover object-top"
              />
              <button
                type="button"
                onClick={() => onChange('')}
                className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
              >
                <i className="ri-close-line text-xs"></i>
              </button>
            </div>
          ) : (
            <div className="w-full h-24 rounded-xl border border-dashed border-gray-300 bg-gray-50 flex items-center justify-center">
              <span className="text-xs text-gray-400">No photo yet</span>
            </div>
          )}
          {/* Also allow pasting a URL */}
          <input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Or paste image URL"
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs text-gray-600 focus:ring-2 focus:ring-green-600 focus:border-transparent outline-none"
          />
        </div>
      </div>

      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}

      {/* Hidden file input — accepts images, opens camera roll on mobile */}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileChange}
      />
    </div>
  );
}

function TractorForm({
  tractor,
  onClose,
  onSave,
}: {
  tractor: Tractor | null;
  onClose: () => void;
  onSave: () => void;
}) {
  const [formData, setFormData] = useState({
    name: tractor?.name || '',
    slug: tractor?.slug || '',
    brand: tractor?.brand || '',
    model: tractor?.model || '',
    year: tractor?.year || new Date().getFullYear(),
    hours: tractor?.hours || 0,
    price: tractor?.price || 0,
    condition: tractor?.condition || 'USED - EXCELLENT',
    description: tractor?.description || '',
    main_image: tractor?.main_image || '',
    image_2: tractor?.image_2 || '',
    image_3: tractor?.image_3 || '',
    image_4: tractor?.image_4 || '',
    engine: tractor?.engine || '',
    horsepower: tractor?.horsepower || '',
    transmission: tractor?.transmission || '',
    hydraulics: tractor?.hydraulics || '',
    weight: tractor?.weight || '',
    fuel_capacity: tractor?.fuel_capacity || '',
    features: tractor?.features?.join(', ') || '',
    contact_phone: tractor?.contact_phone || '(940) 736-5321',
    contact_email: tractor?.contact_email || 'sales@4leaftrading.com',
    is_active: tractor?.is_active ?? true,
  });

  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    const featuresArray = formData.features
      .split(',')
      .map((f) => f.trim())
      .filter((f) => f);

    const dataToSave = {
      ...formData,
      features: featuresArray,
      slug: formData.slug || formData.name.toLowerCase().replace(/\s+/g, '-'),
    };

    let error;
    if (tractor) {
      const { error: updateError } = await supabase
        .from('tractors')
        .update(dataToSave)
        .eq('id', tractor.id);
      error = updateError;
    } else {
      const { error: insertError } = await supabase
        .from('tractors')
        .insert([dataToSave]);
      error = insertError;
    }

    setSaving(false);

    if (error) {
      alert('Error saving tractor: ' + error.message);
      console.error(error);
    } else {
      onSave();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl my-8">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900">
              {tractor ? 'Edit Tractor' : 'Add New Tractor'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 max-h-[70vh] overflow-y-auto">
          <div className="grid grid-cols-2 gap-6">
            <div className="col-span-2">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Tractor Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent outline-none text-sm"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Brand *</label>
              <input
                type="text"
                value={formData.brand}
                onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent outline-none text-sm"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Model *</label>
              <input
                type="text"
                value={formData.model}
                onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent outline-none text-sm"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Year *</label>
              <input
                type="number"
                value={formData.year}
                onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent outline-none text-sm"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Hours *</label>
              <input
                type="number"
                value={formData.hours}
                onChange={(e) => setFormData({ ...formData, hours: parseInt(e.target.value) })}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent outline-none text-sm"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Price *</label>
              <input
                type="number"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) })}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent outline-none text-sm"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Condition *</label>
              <select
                value={formData.condition}
                onChange={(e) => setFormData({ ...formData, condition: e.target.value })}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent outline-none text-sm"
                required
              >
                <option>USED - EXCELLENT</option>
                <option>REFURBISHED</option>
                <option>USED - GOOD</option>
                <option>NEW</option>
              </select>
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Description *</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={4}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent outline-none text-sm resize-none"
                required
              />
            </div>

            {/* Photo Upload Fields */}
            <div className="col-span-2">
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-4">
                <div className="flex items-center gap-2 mb-1">
                  <i className="ri-camera-line text-green-600 text-lg"></i>
                  <span className="text-sm font-bold text-green-800">Photos</span>
                </div>
                <p className="text-xs text-green-700">Tap the camera button to upload photos directly from your phone.</p>
              </div>
              <div className="grid grid-cols-1 gap-5">
                <ImageUploadField
                  label="Main Photo"
                  value={formData.main_image}
                  onChange={(url) => setFormData({ ...formData, main_image: url })}
                  required
                />
                <ImageUploadField
                  label="Photo 2"
                  value={formData.image_2}
                  onChange={(url) => setFormData({ ...formData, image_2: url })}
                />
                <ImageUploadField
                  label="Photo 3"
                  value={formData.image_3}
                  onChange={(url) => setFormData({ ...formData, image_3: url })}
                />
                <ImageUploadField
                  label="Photo 4"
                  value={formData.image_4}
                  onChange={(url) => setFormData({ ...formData, image_4: url })}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Engine</label>
              <input
                type="text"
                value={formData.engine}
                onChange={(e) => setFormData({ ...formData, engine: e.target.value })}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent outline-none text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Horsepower</label>
              <input
                type="text"
                value={formData.horsepower}
                onChange={(e) => setFormData({ ...formData, horsepower: e.target.value })}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent outline-none text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Transmission</label>
              <input
                type="text"
                value={formData.transmission}
                onChange={(e) => setFormData({ ...formData, transmission: e.target.value })}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent outline-none text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Hydraulics</label>
              <input
                type="text"
                value={formData.hydraulics}
                onChange={(e) => setFormData({ ...formData, hydraulics: e.target.value })}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent outline-none text-sm"
              />
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Features (comma separated)</label>
              <textarea
                value={formData.features}
                onChange={(e) => setFormData({ ...formData, features: e.target.value })}
                rows={3}
                placeholder="GPS Guidance System, Climate Controlled Cab, LED Lighting"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent outline-none text-sm resize-none"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Contact Phone</label>
              <input
                type="tel"
                value={formData.contact_phone}
                onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent outline-none text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Contact Email</label>
              <input
                type="email"
                value={formData.contact_email}
                onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent outline-none text-sm"
              />
            </div>

            <div className="col-span-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-600"
                />
                <span className="text-sm font-semibold text-gray-700">Active (visible on website)</span>
              </label>
            </div>
          </div>

          <div className="flex gap-3 mt-8 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors whitespace-nowrap"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            >
              {saving ? 'Saving...' : tractor ? 'Update Tractor' : 'Add Tractor'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
